import { StudentSkillStates } from '../models/StudentSkillState';
import { sortStudentSkillStatesByLabel, sortStudentSkillStatesBySkillName } from '../businessLogicService/sortCriteria';
import { useAppSelector } from '../redux/hooks';
import { selectedAccessToken } from '../redux/reducers/accessTokenSlice';
import { selectUser } from '../redux/reducers/userSlice';
import { selectedTargetGroup } from '../redux/reducers/targetGroupSlice';
import { TargetSkillTypeLabel } from '../models/TargetSkillType';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState, useCallback } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { userTargetGroupGraphQL } from '../graphQLOperations/Dashboard';
import { selectedStudentSkillStateAfterTakeAssessment } from '../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { SkillSubState } from '../models/SkillSubState';
import { inferredOrDiscrepancy } from '../businessLogicService/inferredOrDiscrepancySubstate';
import { splitId } from '../businessLogicService/splitIdsFromBackEnd';
import { DashboardUserTargetGroupQuery } from '../graphQLOperations/__generated__/DashboardUserTargetGroupQuery.graphql';
import { skillsAndTargetsByTargetGroup } from '../graphQLOperations/Dashboard';
import type { DashboardSkillsAndTargetsByTargetGroupQuery } from '../graphQLOperations/__generated__/DashboardSkillsAndTargetsByTargetGroupQuery.graphql';

// Sort function mapping - maps sort keys directly to their corresponding functions
export const SORT_FUNCTIONS = {
  readinessScore: (skills: StudentSkillStates[]) => sortStudentSkillStatesByLabel(skills, 'readinessScore'),
  name: (skills: StudentSkillStates[]) => sortStudentSkillStatesByLabel(skills, 'name'),
  skillName: (skills: StudentSkillStates[]) => [...skills].sort(sortStudentSkillStatesBySkillName),
} as const;

// Type for sortBy using the keys of the SORT_FUNCTIONS object
export type SortByOption = keyof typeof SORT_FUNCTIONS;

export interface UseStudentSkillStatesOptions {
  /** Whether to include extension skills in the results */
  includeExtensions?: boolean;
  /** Sort criteria for the skill states */
  sortBy?: SortByOption;
  /** Unit ID to filter skills by (if provided) */
  unitToFilterBy?: string;
  /** Whether to filter skills by the current target group */
  filterByTargetGroup?: boolean;
}

export interface UseStudentSkillStatesReturn {
  /** The filtered and processed student skill states based on the provided options */
  studentSkillStates: StudentSkillStates[];
  /** Loading state flag */
  isLoading: boolean;
  /** Callback to refresh the data */
  refresh: () => void;
}

export const useStudentSkillStates: (options: UseStudentSkillStatesOptions) => UseStudentSkillStatesReturn = ({
  includeExtensions = false,
  sortBy = 'readinessScore',
  unitToFilterBy = undefined,
  filterByTargetGroup = true,
}) => {
  // Pre-query data:
  const { isAuthenticated } = useAuth0();

  // Redux selectors
  const userRedux = useAppSelector(selectUser);
  const accessTokenRedux = useAppSelector(selectedAccessToken);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const sssToUpdate = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);

  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const tgId = `TargetGroupNode:${splitId(targetGroupRedux?.id || '')}`;
  const queryFetchKey = `${accessTokenRedux || ''}-${refreshKey}-${JSON.stringify(sssToUpdate)}`;

  // The below query runs automatically and triggers rerender with returned data when complete.
  const sssFilteredByRoadmapQuery = useLazyLoadQuery<DashboardUserTargetGroupQuery>(
    userTargetGroupGraphQL,
    {
      studentId: `StudentNode:${userRedux?.id}`,
      targetGroupId: tgId,
    },
    {
      fetchKey: queryFetchKey, // Trigger refetch (on hook call) if the most recent assessment result has changed.
      // NOTE: Triggering refetch doesn't mean that the Learnosity updates have reached the backend yet.
      // Theoretically, a student could complete multiple assessments before the backend updates, and the frontend would only show the most recent.
      fetchPolicy: 'store-or-network', // Only refetch if the query changes or fetchKey changes.
    },
  );

  const skillsAndTargetsByTargetGroupQuery = useLazyLoadQuery<DashboardSkillsAndTargetsByTargetGroupQuery>(
    skillsAndTargetsByTargetGroup,
    { targetGroupId: tgId },
    {
      fetchKey: queryFetchKey,
      fetchPolicy: 'store-or-network',
    },
  );

  type SkillsAndTargetsEntry = NonNullable<
    NonNullable<DashboardSkillsAndTargetsByTargetGroupQuery['response']['skillsAndTargetsByTargetGroup']>[number]
  >;

  type RawTargetSkill = NonNullable<NonNullable<SkillsAndTargetsEntry['targetSkills']>[number]>;

  const mapTargetSkillToModel = (targetSkill: RawTargetSkill) => {
    const target = targetSkill.target;
    const targetSkillType = targetSkill.targetSkillType;

    return {
      target: {
        id: target?.id ?? '',
        name: target?.name ?? '',
        targetGroup: {
          id: targetGroupRedux?.id ?? '',
          name: targetGroupRedux?.name ?? '',
        },
      },
      targetSkillType: {
        id: targetSkillType?.id ?? '',
        name: targetSkillType?.name ?? '',
      },
    };
  };

  const skillTargetsBySkillCode = useMemo(() => {
    const targetsBySkillCodeMap = new Map<string, RawTargetSkill[]>();
    const entries = skillsAndTargetsByTargetGroupQuery?.skillsAndTargetsByTargetGroup ?? [];

    entries
      ?.filter((entry): entry is SkillsAndTargetsEntry => !!entry)
      .forEach(entry => {
        const skillCode = entry.skillInfo?.code;
        if (!skillCode) {
          return;
        }

        const targetSkills = (entry.targetSkills ?? []).filter(
          (targetSkill): targetSkill is RawTargetSkill => !!targetSkill,
        );

        targetsBySkillCodeMap.set(skillCode, targetSkills);
      });

    return targetsBySkillCodeMap;
  }, [skillsAndTargetsByTargetGroupQuery]);

  let studentSkillStatesQueryResult: StudentSkillStates[] = [];
  if (sssFilteredByRoadmapQuery.skillStatesByStudentId) {
    studentSkillStatesQueryResult = convertGraphQLToReduxSkillStates(sssFilteredByRoadmapQuery.skillStatesByStudentId);
  }

  // Refresh callback function using aggressive cache bypass
  const refresh = useCallback(() => {
    if (!isAuthenticated || !accessTokenRedux || !userRedux?.id) {
      return;
    }

    setIsLoading(true);

    const timestamp = Date.now();
    setRefreshKey(timestamp);
  }, [isAuthenticated, accessTokenRedux, userRedux?.id]);

  // Update the student skill states if the studentSkillAfterTakeAssessment is filled.
  const mutatedSkillStates = useMemo(() => {
    return studentSkillStatesQueryResult.map(skillState => {
      if (
        skillState.skill.code === sssToUpdate.previousState?.skill?.code &&
        sssToUpdate.nextState?.skillAssessment?.score !== skillState.skillAssessment?.score
      ) {
        let skillSubstateUpdated: SkillSubState = skillState.skillSubstate;

        if (inferredOrDiscrepancy(skillState.skillSubstate.id)) {
          //Assing a provisory skillSubstate to skip mapSkillStatus, when inference engine impact, the next variable not be assigned.
          skillSubstateUpdated = {
            id: 'SkillSubstateNode:1',
          };
        }

        return {
          ...skillState,
          skillAssessment: sssToUpdate?.nextState?.skillAssessment,
          skillSubstate: skillSubstateUpdated,
        };
      }
      return skillState;
    });
  }, [sssToUpdate, studentSkillStatesQueryResult]);
  studentSkillStatesQueryResult = mutatedSkillStates;

  const studentSkillStatesWithTargets: StudentSkillStates[] = useMemo(() => {
    return studentSkillStatesQueryResult.map(skillState => {
      const skillCode = skillState.skill?.code || '';
      const rawTargetSkills = skillTargetsBySkillCode.get(skillCode) ?? [];
      const mappedTargetSkills = rawTargetSkills.map(mapTargetSkillToModel);

      let filteredTargetSkills = mappedTargetSkills;

      if (targetGroupRedux) {
        filteredTargetSkills = filteredTargetSkills.filter(
          targetSkill => targetSkill.target.targetGroup?.id === targetGroupRedux.id,
        );
      }

      if (unitToFilterBy) {
        filteredTargetSkills = filteredTargetSkills.filter(targetSkill => targetSkill.target.id === unitToFilterBy);
      }

      const targetSkillTypeName =
        (
          filteredTargetSkills.find(targetSkill => targetSkill.targetSkillType?.name === TargetSkillTypeLabel.TARGET) ??
          filteredTargetSkills[0]
        )?.targetSkillType?.name ?? null;

      return {
        ...skillState,
        skill: {
          ...skillState.skill,
          targetSkills: mappedTargetSkills,
        },
        targetSkillType: targetSkillTypeName,
      };
    });
  }, [studentSkillStatesQueryResult, skillTargetsBySkillCode, targetGroupRedux, unitToFilterBy]);

  const processedStudentSkillStates: StudentSkillStates[] = useMemo(() => {
    const studentSkillStates = studentSkillStatesWithTargets || [];

    let result = studentSkillStates;

    // Step 1: Sort the skill states
    result = sortBy ? SORT_FUNCTIONS[sortBy](result) : result;

    // Step 2: Filter by unit if specified
    if (unitToFilterBy) {
      result = result.filter(
        skill => skill.skill?.targetSkills?.some(targetSkill => targetSkill.target?.id === unitToFilterBy),
      );
    }

    // Step 3: Filter by extension skills if specified
    if (!includeExtensions) {
      result = result.filter(skill => {
        return skill.targetSkillType !== TargetSkillTypeLabel.EXTENSION;
      });
    }

    return result;
  }, [studentSkillStatesWithTargets, sortBy, filterByTargetGroup, includeExtensions, unitToFilterBy]);

  return { studentSkillStates: processedStudentSkillStates, isLoading, refresh };
};

// Type for the GraphQL query result
export type GraphQLSkillState = NonNullable<
  DashboardUserTargetGroupQuery['response']['skillStatesByStudentId']
>[number];

/**
 * Converts GraphQL query results to Redux store compatible StudentSkillStates
 * This function handles the type incompatibilities between GraphQL and Redux types
 *
 * Key conversions:
 * - readinessScore: any | null | undefined -> number | undefined
 * - modifiedOn: any | null | undefined -> string | null
 * - createdOn: any -> string
 * - skillAssessment: inline object -> SkillAssessment | null
 * - Handles nullable nested objects safely
 */
export const convertGraphQLToReduxSkillStates = (
  graphqlSkillStates: readonly GraphQLSkillState[],
): StudentSkillStates[] => {
  return graphqlSkillStates.map(graphqlSkill => ({
    id: graphqlSkill.id,
    inProgress: graphqlSkill.inProgress,
    readinessScore: typeof graphqlSkill.readinessScore === 'number' ? graphqlSkill.readinessScore : undefined,
    modifiedOn: typeof graphqlSkill.modifiedOn === 'string' ? graphqlSkill.modifiedOn : null,
    createdOn: typeof graphqlSkill.createdOn === 'string' ? graphqlSkill.createdOn : '',
    skillAssessment: graphqlSkill.skillAssessment
      ? {
          id: '', // GraphQL query doesn't return id, so we provide a default
          score: graphqlSkill.skillAssessment.score,
          isComplete: graphqlSkill.skillAssessment.isComplete,
        }
      : null,
    skillSubstate: {
      id: graphqlSkill.skillSubstate?.id || '',
      name: graphqlSkill.skillSubstate?.name || '',
      skillState: {
        id: graphqlSkill.skillSubstate?.skillState?.id || '',
        name: graphqlSkill.skillSubstate?.skillState?.name || '',
      },
    },
    skill: {
      id: graphqlSkill.skill?.id || '',
      code: graphqlSkill.skill?.code || '',
      name: graphqlSkill.skill?.name || '',
      difficulty: graphqlSkill.skill?.difficulty,
      targetSkills: [],
    },
  }));
};
